import { useEffect, useRef } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useKeycloak } from '@react-keycloak/web';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { SessionExpiredError } from './apiUtils';

// One page of a list table (Routes, Connections, Sources, Workspaces). The
// query key holds the filters and the page offset, so changing either loads
// that page and a late answer for an older filter can never overwrite the
// newer one. The rows on screen stay while the next page loads; if it fails,
// the old rows go (they belong to another page or filter) and `error` says so.
export const useListQuery = <T>(
  queryKey: readonly unknown[],
  load: (token: string) => Promise<{ results: T[]; count: number }>,
  enabled: boolean = true,
) => {
  const { keycloak } = useKeycloak();
  const { t } = useTranslation();
  const token = keycloak.token;
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const data = await load(token as string);
      // Undefined means the session expired (see SessionExpiredError).
      if (data === undefined) throw new SessionExpiredError();
      return data;
    },
    enabled: enabled && !!token && !keycloak.isTokenExpired(),
    placeholderData: keepPreviousData,
  });

  // Latched: once the first answer arrives (even a failed one) the page is
  // past its skeleton, and a new filter or page doesn't send it back.
  const settledOnce = useRef(false);
  if (query.isFetched) settledOnce.current = true;

  // Errors already in the cache when the page mounted, or already reported,
  // don't toast again.
  // Strictly before mount: an error in the same millisecond is this page's own.
  const mountedAt = useRef(Date.now());
  const lastToastedAt = useRef(0);
  useEffect(() => {
    // An expired session is the login modal's to report, not a failure.
    if (!query.error || query.error instanceof SessionExpiredError) return;
    if (
      query.errorUpdatedAt < mountedAt.current ||
      query.errorUpdatedAt <= lastToastedAt.current
    )
      return;
    lastToastedAt.current = query.errorUpdatedAt;
    toast.error(
      t('Something went wrong. Please try again or contact support.'),
      { position: toast.POSITION.TOP_RIGHT },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.errorUpdatedAt]);

  const shown = query.data;
  return {
    results: shown?.results ?? [],
    count: shown?.count ?? 0,
    // A request is in flight (first load, new page/filter or refresh).
    loading: query.isFetching,
    // The first answer arrived, successful or not, and stays true after.
    settled: settledOnce.current,
    error: query.isError,
    refresh: query.refetch,
  };
};
